<!--
  烂尾单分析的添加 / 编辑表单弹窗

  原型字段：*客户（下拉）、*项目（下拉）、联系人、*原因（下拉）、*原因详细说明（文本域）、解决方案（文本域）

  ★ 「项目」下拉按所选「客户」过滤（后端 /crm/order/simple-list?customerId=），
    切换客户会清空已选项目；后端还有一次「项目必须属于该客户」的兜底校验。
-->
<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="680px">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="120px"
    >
      <el-form-item label="客户" prop="customerId">
        <el-select
          v-model="formData.customerId"
          class="!w-full"
          clearable
          filterable
          placeholder="请选择客户"
          @change="handleCustomerChange"
        >
          <el-option
            v-for="item in customerOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="项目" prop="orderId">
        <el-select
          v-model="formData.orderId"
          class="!w-full"
          clearable
          filterable
          :disabled="!formData.customerId"
          placeholder="请先选择客户，再选择项目"
        >
          <el-option
            v-for="item in orderOptions"
            :key="item.id"
            :label="item.projectName"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="联系人" prop="contactName">
        <el-input v-model="formData.contactName" placeholder="请输入联系人" />
      </el-form-item>
      <el-form-item label="原因" prop="reasonId">
        <el-select
          v-model="formData.reasonId"
          class="!w-full"
          clearable
          filterable
          placeholder="请选择原因"
        >
          <el-option
            v-for="item in reasonOptions"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="原因详细说明" prop="reasonDetail">
        <el-input
          v-model="formData.reasonDetail"
          :rows="4"
          placeholder="请输入原因详细说明"
          type="textarea"
        />
      </el-form-item>
      <el-form-item label="解决方案" prop="solution">
        <el-input
          v-model="formData.solution"
          :rows="4"
          placeholder="请输入解决方案"
          type="textarea"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">提 交</el-button>
      <el-button @click="dialogVisible = false">结 束</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { OrderStallAnalysisApi } from '@/api/crm/orderStallAnalysis'
import { OrderStallReasonApi } from '@/api/crm/orderStallReason'
import * as CustomerApi from '@/api/crm/customer'
import * as OrderApi from '@/api/crm/order'

defineOptions({ name: 'StallAnalysisForm' })

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗

const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中
const formType = ref('') // 表单的类型：create - 新增；update - 修改
const customerOptions = ref<any[]>([]) // 客户下拉
const orderOptions = ref<any[]>([]) // 项目（订单）下拉
const reasonOptions = ref<any[]>([]) // 原因下拉

const formData = ref<any>({
  id: undefined,
  customerId: undefined,
  orderId: undefined,
  contactName: undefined,
  reasonId: undefined,
  reasonDetail: undefined,
  solution: undefined
})
const formRules = reactive({
  customerId: [{ required: true, message: '客户不能为空', trigger: 'change' }],
  orderId: [{ required: true, message: '项目不能为空', trigger: 'change' }],
  reasonId: [{ required: true, message: '原因不能为空', trigger: 'change' }],
  reasonDetail: [{ required: true, message: '原因详细说明不能为空', trigger: 'blur' }]
})
const formRef = ref() // 表单 Ref

/** 加载指定客户下的项目（订单）下拉 */
const loadOrderOptions = async (customerId?: number) => {
  if (!customerId) {
    orderOptions.value = []
    return
  }
  orderOptions.value = (await OrderApi.getOrderSimpleList({ customerId })) || []
}

/** 切换客户：清空已选项目，并重新拉取项目下拉 */
const handleCustomerChange = async (customerId?: number) => {
  formData.value.orderId = undefined
  await loadOrderOptions(customerId)
}

/** 打开弹窗 */
const open = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = type === 'create' ? '新增烂尾单分析' : '编辑烂尾单分析'
  formType.value = type
  resetForm()
  // 加载下拉数据
  formLoading.value = true
  try {
    customerOptions.value = await CustomerApi.getCustomerSimpleList()
    reasonOptions.value = await OrderStallReasonApi.getOrderStallReasonListAll()
    // 修改时，加载数据
    if (id) {
      const data = await OrderStallAnalysisApi.getOrderStallAnalysis(id)
      formData.value = {
        id: data.id,
        customerId: data.customerId,
        orderId: data.orderId,
        contactName: data.contactName,
        reasonId: data.reasonId,
        reasonDetail: data.reasonDetail,
        solution: data.solution
      }
      await loadOrderOptions(data.customerId)
    }
  } finally {
    formLoading.value = false
  }
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  // 校验表单
  await formRef.value.validate()
  // 提交请求
  formLoading.value = true
  try {
    const data = { ...formData.value }
    if (formType.value === 'create') {
      await OrderStallAnalysisApi.createOrderStallAnalysis(data)
      message.success(t('common.createSuccess'))
    } else {
      await OrderStallAnalysisApi.updateOrderStallAnalysis(data)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    // 发送操作成功的事件
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = {
    id: undefined,
    customerId: undefined,
    orderId: undefined,
    contactName: undefined,
    reasonId: undefined,
    reasonDetail: undefined,
    solution: undefined
  }
  orderOptions.value = []
  formRef.value?.resetFields()
}
</script>
